import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ArrowRight, Coffee, Globe, Calculator, BookOpen, Sparkles, GraduationCap, Library, BookMarked, Lightbulb, Users, Trophy, Medal, MessageSquare, FileText, Award } from "lucide-react";
import ShareButton from "@/components/ShareButton";
import TrendingRecipes from "@/components/TrendingRecipes";
import YesterdaysWinner from "@/components/YesterdaysWinner";
import ContinueLearning from "@/components/ContinueLearning";
import Seo from "@/components/Seo";
import { endpoints } from "@/api";

const HERO_IMG = "https://images.pexels.com/photos/7125438/pexels-photo-7125438.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HARVEST_IMG = "https://images.pexels.com/photos/30658829/pexels-photo-30658829.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const V60_IMG = "https://images.unsplash.com/photo-1620051787220-50768889e32c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxwb3VyJTIwb3ZlciUyMGNvZmZlZSUyMFY2MHxlbnwwfHx8fDE3ODA3NDk4MjJ8MA&ixlib=rb-4.1.0&q=85";
const ESPRESSO_IMG = "https://images.unsplash.com/photo-1616388761741-a5936c6f61f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxlc3ByZXNzbyUyMHNob3QlMjBwdWxsfGVufDB8fHx8MTc4MDc0OTgyMnww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";

  return (
    <div data-testid="home-page">
      <Seo
        title={isRTL ? "أكاديمية علوم القهوة المختصة بالعربية" : "Specialty Coffee Science Academy"}
        description={isRTL
          ? "أكاديمية عبدالله الكاساني لعلم القهوة المختصة — دورات SCA معتمدة، دورة باريستا أونلاين، دورة تحميص القهوة المختصة من الصفر، حاسبة استخلاص القهوة (TDS وEY)، تحليل القهوة الحسي، موسوعة شاملة من البذرة إلى الفنجان، مجتمع وتحدّيات أسبوعية. تعلّم علوم القهوة باللغة العربية."
          : "Abdullah Coffee Science Academy — SCA-aligned barista & roasting courses, TDS/EY extraction calculator, sensory training, full from-seed-to-cup encyclopedia, weekly brew challenges and active community. Specialty coffee education in Arabic & English."}
        keywords="علوم القهوة, علم القهوة, دورات القهوة المختصة, أكاديمية القهوة, تعلم القهوة المختصة, دورات باريستا, دورة باريستا أونلاين, أفضل دورة باريستا أونلاين, دورة تحميص القهوة, تحميص القهوة المختصة, شرح التحميص للقهوة المختصة, تعلم تحميص القهوة من الصفر, استخلاص القهوة, كيف أحسب نسبة الاستخلاص في القهوة, ما هو TDS في القهوة, حاسبة TDS, تحليل القهوة الحسي, دورة علوم القهوة باللغة العربية, SCA Coffee Courses, Abdullah Coffee Science"
        path="/"
      />
      {/* HERO */}
      <section className="relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 fade-up">
            <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-6">
              {isRTL ? "علم · حرفة · مجتمع" : "Science · Craft · Community"}
            </div>
            <h1 className={`${serifClass} text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight text-[#2A2421]`}>
              {t.home.heroTitle}
            </h1>
            <p className="mt-8 max-w-xl text-[#5C544F] text-lg leading-relaxed">
              {t.home.heroLead}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/journey" data-testid="hero-explore-btn">
                <Button size="lg" className="rounded-full bg-[#C86240] hover:bg-[#b15637] text-white px-7 py-6 group">
                  {t.home.ctaExplore}
                  <ArrowRight className="w-4 h-4 ms-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/recipes" data-testid="hero-recipes-btn">
                <Button size="lg" variant="outline" className="rounded-full border-[#2A2421]/20 text-[#2A2421] hover:bg-[#2A2421]/5 px-7 py-6">
                  {t.home.ctaRecipes}
                </Button>
              </Link>
              <ShareButton
                title="عبدالله | علم القهوة"
                text={isRTL ? "موسوعة شاملة لعلم القهوة من البذرة حتى الفنجان" : "From seed to cup — a complete coffee science encyclopedia."}
                url="https://abdullah-coffee-science.academy/"
                label={isRTL ? "مشاركة الموقع" : "Share"}
                variant="outline"
                iconSize={18}
                className="px-6 py-3 text-sm"
                testId="hero-share-btn"
              />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden soft-card">
              <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -start-6 w-40 aspect-square rounded-xl overflow-hidden soft-card hidden md:block">
              <img src={V60_IMG} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* PUBLIC STATS COUNTER STRIP */}
      <StatsStrip isRTL={isRTL} />

      {/* CONTINUE LEARNING (only visible to logged-in users with progress) */}
      <ContinueLearning />

      {/* WELCOME / WHAT THIS SITE OFFERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20" data-testid="welcome-section">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C86240] mb-4">
              {isRTL ? "أهلًا بك" : "Welcome"}
            </div>
            <h2 className={`${serifClass} text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#2A2421]`}>
              {isRTL ? "كل ما تحتاجه عن القهوة، في مكان واحد." : "Everything you need about coffee, in one place."}
            </h2>
            <p className="mt-6 text-[#5C544F] leading-relaxed">
              {isRTL
                ? "هذا الموقع ليس مدوّنة عادية — بل رحلة تعليمية كاملة من البذرة إلى الفنجان: علم، حِرفة، وتجربة عملية، باللغتين العربية والإنجليزية."
                : "This isn't a typical blog — it's a complete learning journey from seed to cup: science, craft, and hands-on practice, in both Arabic and English."}
            </p>
            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              <span className="px-3 py-1 rounded-full bg-[#C86240]/10 text-[#C86240]">
                {isRTL ? "✓ ثنائي اللغة" : "✓ Bilingual"}
              </span>
              <span className="px-3 py-1 rounded-full bg-[#2A2421]/8 text-[#2A2421]">
                {isRTL ? "✓ علمي ومدعوم بـ SCA" : "✓ SCA-aligned"}
              </span>
              <span className="px-3 py-1 rounded-full bg-[#4A5D4E]/15 text-[#4A5D4E]">
                {isRTL ? "✓ مجاني ومجتمعي" : "✓ Free & community-driven"}
              </span>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {[
              { to: "/journey", icon: BookOpen, title_ar: "رحلة البن", title_en: "Coffee Journey", desc_ar: "من المزرعة للكوب", desc_en: "Farm to cup story" },
              { to: "/encyclopedia", icon: Library, title_ar: "الموسوعة", title_en: "Encyclopedia", desc_ar: "مصطلحات ومفاهيم علمية", desc_en: "Terms & science" },
              { to: "/academy", icon: GraduationCap, title_ar: "الأكاديمي", title_en: "Academy", desc_ar: "تأهيل SCA ومسارات", desc_en: "SCA paths & training" },
              { to: "/recipes", icon: BookMarked, title_ar: "الوصفات", title_en: "Recipes", desc_ar: "وصفات عالمية بتقييمات", desc_en: "Curated brew recipes" },
              { to: "/calculator", icon: Calculator, title_ar: "حاسبة الاستخلاص", title_en: "Extraction Calculator", desc_ar: "TDS · EY · نصائح AI", desc_en: "TDS · EY · AI tips" },
              { to: "/challenge", icon: Trophy, title_ar: "التحدي الأسبوعي", title_en: "Weekly Challenge", desc_ar: "نقاط وقائمة متصدّرين", desc_en: "Score on leaderboard" },
              { to: "/achievements", icon: Medal, title_ar: "الإنجازات", title_en: "Badges", desc_ar: "افتح ٩ شارات", desc_en: "Unlock 9 badges" },
              { to: "/community", icon: MessageSquare, title_ar: "المجتمع", title_en: "Community", desc_ar: "رسائل ومناقشات", desc_en: "Updates & replies" },
              { to: "/blog", icon: FileText, title_ar: "المدوّنة", title_en: "Blog", desc_ar: "مقالات وتحاليل", desc_en: "Deep articles" },
              { to: "/assistant", icon: Sparkles, title_ar: "الباريستا الذكي", title_en: "AI Barista", desc_ar: "اسأل أي سؤال", desc_en: "Ask anything" },
            ].map((f, i) => (
              <Link
                key={f.to}
                to={f.to}
                data-testid={`welcome-card-${f.to.replace("/", "")}`}
                className="group rounded-xl bg-white border border-[#2A2421]/10 p-4 hover:border-[#C86240]/40 hover:-translate-y-0.5 transition-all flex items-start gap-3"
              >
                <div className="shrink-0 w-9 h-9 rounded-lg bg-[#C86240]/10 text-[#C86240] flex items-center justify-center group-hover:bg-[#C86240] group-hover:text-white transition-colors">
                  <f.icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#2A2421] leading-tight">
                    {isRTL ? f.title_ar : f.title_en}
                  </div>
                  <div className="text-xs text-[#5C544F] mt-0.5 leading-snug">
                    {isRTL ? f.desc_ar : f.desc_en}
                  </div>
                </div>
                <ArrowRight className={`w-3.5 h-3.5 text-[#5C544F] mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${isRTL ? "rotate-180" : ""}`} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="relative rounded-3xl overflow-hidden bg-[#2A2421] text-[#F9F6F0] px-6 sm:px-10 lg:px-14 py-14 lg:py-20">
          <div
            aria-hidden
            className="absolute inset-0 opacity-25 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 60% 60% at 90% 0%, #C86240 0%, transparent 55%), radial-gradient(ellipse 50% 50% at 0% 100%, #4A5D4E 0%, transparent 65%)",
            }}
          />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C86240]/15 border border-[#C86240]/30 text-[#E89A7C] text-[11px] uppercase tracking-[0.25em] mb-6">
              <Sparkles size={12} />
              {t.home.missionTag}
            </div>
            <h2 className={`${serifClass} text-3xl sm:text-4xl lg:text-5xl leading-[1.1] max-w-4xl mb-6`}>
              {t.home.missionTitle}
            </h2>
            <p className="text-base sm:text-lg text-[#E0D7C8] leading-relaxed max-w-3xl">
              {t.home.missionLead}
            </p>

            <div className="mt-12 grid sm:grid-cols-3 gap-4 sm:gap-5">
              <MissionPillar
                icon={BookMarked}
                title={t.home.missionPillar1Title}
                body={t.home.missionPillar1Body}
                testId="mission-pillar-1"
              />
              <MissionPillar
                icon={Lightbulb}
                title={t.home.missionPillar2Title}
                body={t.home.missionPillar2Body}
                testId="mission-pillar-2"
              />
              <MissionPillar
                icon={Users}
                title={t.home.missionPillar3Title}
                body={t.home.missionPillar3Body}
                testId="mission-pillar-3"
              />
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY TEASER - bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#4A5D4E] mb-3">{t.home.sectionJourney}</div>
            <h2 className={`${serifClass} text-3xl lg:text-5xl text-[#2A2421] max-w-2xl`}>
              {t.home.sectionJourneyLead}
            </h2>
          </div>
          <Link to="/journey" className="hidden sm:flex items-center gap-2 text-sm text-[#C86240] hover:underline" data-testid="journey-section-link">
            {isRTL ? "اعرف أكثر" : "Read more"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          <div className="md:col-span-4 relative aspect-[16/10] rounded-2xl overflow-hidden soft-card">
            <img src={HARVEST_IMG} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
            <div className="absolute bottom-6 start-6 end-6 text-white">
              <div className="text-xs uppercase tracking-[0.25em] opacity-80">{isRTL ? "02 · الحصاد" : "02 · Harvest"}</div>
              <div className={`${serifClass} text-2xl lg:text-3xl mt-1`}>
                {isRTL ? "أيدٍ تختار النضج فقط" : "Hands that pick only ripe"}
              </div>
            </div>
          </div>
          <div className="md:col-span-2 grid grid-rows-2 gap-6">
            <div className="rounded-2xl bg-[#4A5D4E] text-[#F9F6F0] p-6 flex flex-col justify-between">
              <div className="text-xs uppercase tracking-[0.25em] opacity-80">{isRTL ? "04 · التحميص" : "04 · Roast"}</div>
              <div className={`${serifClass} text-2xl`}>
                {isRTL ? "حرارة، وقت، توازن." : "Heat, time, balance."}
              </div>
            </div>
            <div className="rounded-2xl bg-[#C86240] text-white p-6 flex flex-col justify-between">
              <div className="text-xs uppercase tracking-[0.25em] opacity-80">{isRTL ? "05 · التحضير" : "05 · Brew"}</div>
              <div className={`${serifClass} text-2xl`}>
                {isRTL ? "خمسة متغيرات. فنجان واحد." : "Five variables. One cup."}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TOOLS SECTION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[#4A5D4E] mb-3">{t.home.sectionTools}</div>
          <h2 className={`${serifClass} text-3xl lg:text-5xl text-[#2A2421] max-w-2xl`}>{t.home.sectionToolsLead}</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <ToolCard to="/encyclopedia" icon={Library} title={isRTL ? "موسوعة القهوة" : "Encyclopedia"} body={isRTL ? "18 موديول من البذرة حتى الكأس + معايير SCA." : "18 modules from seed to cup + SCA standards."} testId="tool-encyclopedia" />
          <ToolCard to="/academy" icon={GraduationCap} title={isRTL ? "الأكاديمي" : "Academy"} body={isRTL ? "دورتان: الكاساني وCSP الاحترافية." : "Two tracks: Kasani & CSP Professional."} testId="tool-academy" />
          <ToolCard to="/countries" icon={Globe} title={isRTL ? "البلدان والمحاصيل" : "Countries & crops"} body={isRTL ? "16 أصلاً، نكهات وارتفاعات." : "16 origins, flavours & altitudes."} testId="tool-countries" />
          <ToolCard to="/calculator" icon={Calculator} title={isRTL ? "حاسبة الريشيو" : "Ratio calculator"} body={isRTL ? "كل طرق التحضير. نسب دقيقة." : "Every brew method. Precise ratios."} testId="tool-calculator" />
        </div>
      </section>

      {/* TRENDING (most-loved) RECIPES */}
      <TrendingRecipes />
      <YesterdaysWinner />

      {/* RECIPE TEASER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden soft-card">
            <img src={ESPRESSO_IMG} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-3">
            {isRTL ? "وصفات · مجتمع" : "Recipes · Community"}
          </div>
          <h2 className={`${serifClass} text-3xl lg:text-5xl text-[#2A2421] leading-[1.05]`}>
            {isRTL ? "وصفات منتقاة، ومجتمع يشارك تجاربه." : "Curated classics, and a community sharing their craft."}
          </h2>
          <p className="mt-6 text-[#5C544F] max-w-lg leading-relaxed">
            {isRTL ? "اطّلع على وصفات منتقاة، أو سجّل الدخول وأضف وصفتك ليُلهم الآخرين." : "Browse hand-picked recipes, or sign in and add your own to inspire the next brewer."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/recipes" data-testid="recipes-cta">
              <Button className="rounded-full bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0] px-6 py-5">
                <BookOpen className="w-4 h-4 me-2" /> {t.recipes.title}
              </Button>
            </Link>
            <Link to="/recipes/new" data-testid="add-recipe-cta">
              <Button variant="outline" className="rounded-full border-[#2A2421]/20 text-[#2A2421] hover:bg-[#2A2421]/5 px-6 py-5">
                <Coffee className="w-4 h-4 me-2" /> {t.recipes.add}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const ToolCard = ({ to, icon: Icon, title, body, testId }) => (
  <Link to={to} data-testid={testId} className="group block soft-card rounded-2xl p-7 hover:-translate-y-1 transition-transform">
    <div className="w-10 h-10 rounded-full bg-[#F9F6F0] border border-[#2A2421]/10 flex items-center justify-center mb-5">
      <Icon className="w-5 h-5 text-[#C86240]" />
    </div>
    <div className="text-lg font-semibold text-[#2A2421] mb-2">{title}</div>
    <div className="text-sm text-[#5C544F] leading-relaxed">{body}</div>
    <div className="mt-6 flex items-center gap-1 text-sm text-[#C86240]">
      <span>{title}</span> <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
    </div>
  </Link>
);

const MissionPillar = ({ icon: Icon, title, body, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-sm p-5 sm:p-6 hover:bg-white/[0.07] transition-colors"
  >
    <div className="w-10 h-10 rounded-xl bg-[#C86240]/15 border border-[#C86240]/30 flex items-center justify-center text-[#E89A7C] mb-4">
      <Icon className="w-5 h-5" />
    </div>
    <div className="text-base sm:text-lg font-semibold text-[#F9F6F0] mb-2">{title}</div>
    <div className="text-sm text-[#C5BCAE] leading-relaxed">{body}</div>
  </div>
);

function AnimatedCounter({ value, duration = 1400 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const from = 0;
            const to = Number(value) || 0;
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
              setDisplay(Math.round(from + (to - from) * eased));
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{display.toLocaleString()}</span>;
}

function StatsStrip({ isRTL }) {
  const { data, isLoading } = useQuery({
    queryKey: ["public-stats"],
    queryFn: endpoints.publicStats,
    staleTime: 5 * 60 * 1000,
  });
  if (isLoading || !data) {
    return (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-12" aria-hidden>
        <div className="h-24" />
      </section>
    );
  }
  const items = [
    { key: "recipes", icon: BookMarked, label_ar: "وصفة", label_en: "Recipes", value: data.recipes },
    { key: "users", icon: Users, label_ar: "مستخدم", label_en: "Members", value: data.users },
    { key: "brews", icon: Coffee, label_ar: "تخميرة مسجّلة", label_en: "Brews logged", value: data.brews },
    { key: "badges", icon: Award, label_ar: "شارة مُفعّلة", label_en: "Badges earned", value: data.badges_unlocked },
    { key: "countries", icon: Globe, label_ar: "دولة منشأ", label_en: "Origin countries", value: data.countries },
  ].filter((i) => (i.value || 0) > 0); // hide zero stats
  if (items.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-12" data-testid="public-stats-strip">
      <div className="rounded-2xl bg-white border border-[#2A2421]/10 px-5 sm:px-8 py-6 shadow-sm">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C86240] text-center mb-4">
          {isRTL ? "أرقام المجتمع" : "Community pulse"}
        </div>
        <div className={`grid gap-4 ${items.length >= 5 ? "grid-cols-2 sm:grid-cols-5" : `grid-cols-2 sm:grid-cols-${items.length}`}`}>
          {items.map((s) => (
            <div key={s.key} className="text-center" data-testid={`stat-${s.key}`}>
              <s.icon className="w-4 h-4 text-[#C86240] mx-auto mb-1.5" />
              <div className="text-2xl sm:text-3xl font-semibold text-[#2A2421] tabular-nums">
                <AnimatedCounter value={s.value} />
                <span className="text-[#C86240]">+</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#5C544F] mt-1">
                {isRTL ? s.label_ar : s.label_en}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

